import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, today } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Autocomplete } from "../Autocomplete";
import { OtpVerify } from "./OtpVerify";
import { SignaturePad } from "./SignaturePad";
import { sendGpCopy, printThermal, thermalMm, useBrand } from "./GatePassView";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";
const pill = (a) => `h-10 rounded-md border text-xs font-medium transition-colors ${a ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`;
const TYPES = [{ v: "invoice", l: "Invoice" }, { v: "uchanti", l: "Uchanti (without invoice)" }, { v: "foc", l: "FOC (free of cost)" }, { v: "no_work", l: "No work done" }, { v: "company_vehicle", l: "Company bike / service van" }, { v: "outside_work", l: "Outside work" }, { v: "employee", l: "Employee gate pass" }, { v: "trial", l: "Trial" }];
const STAFF_T = ["outside_work", "employee", "trial"];
const PAYS = ["cash", "qr", "bank", "upi", "credit", "foc", "amc", "warranty"];
const localToday = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const blank = () => ({ gp_type: "invoice", date: localToday(), vehicle_number: "", driver_name: "", driver_mobile: "", party_name: "", account_name: "", payment_type: "cash", amount: "", reference: "", job_card_number: "", invoice_no: "", current_km: "", to_location: "", guarantor_name: "", guarantor_mobile: "", remarks: "", employee_name: "", ow_vehicle: "company" });

const F = ({ title, req, children }) => <div><label className={label}>{title}{req && <span className="text-destructive"> *</span>}</label>{children}</div>;

const VehicleFields = ({ f, set, meta }) => (f.gp_type === "company_vehicle" ? (
  <div className="grid grid-cols-2 gap-3">
    <F title="Company vehicle" req>
      <select data-testid="gp-company-vehicle" className={field} value={f.vehicle_number} onChange={(e) => set({ vehicle_number: e.target.value })}>
        <option value="">Select vehicle…</option>
        {meta.vehicles.map((v) => <option key={v.number} value={v.number}>{v.number}{v.name ? ` · ${v.name}` : ""}</option>)}
      </select>
      {meta.vehicles.length === 0 && <p className="text-xs text-muted-foreground mt-1">Admin can add company vehicles in Gate Pass settings.</p>}
    </F>
    <F title="Current kilometre" req><input data-testid="gp-km" type="number" min="0" className={field + " num"} value={f.current_km} onChange={(e) => set({ current_km: e.target.value })} /></F>
    <F title="From"><input data-testid="gp-from" className={field + " bg-muted"} value={meta.workshop || "This workshop"} readOnly /></F>
    <F title="To (location)" req><input data-testid="gp-to" className={field} value={f.to_location} onChange={(e) => set({ to_location: e.target.value })} placeholder="Where is it going?" /></F>
  </div>
) : (
  <F title="Vehicle number" req><Autocomplete field="vehicle" testid="gp-vehicle" value={f.vehicle_number} onChange={(v) => set({ vehicle_number: v })} placeholder="DL01AB1234" /></F>
));

const PaymentFields = ({ f, set }) => (
  <>
    <F title="Payment type" req>
      <div className="grid grid-cols-4 gap-1.5">
        {PAYS.map((p) => <button key={p} type="button" data-testid={`gp-pay-${p}`} onClick={() => set({ payment_type: p })} className={`${pill(f.payment_type === p)} uppercase`}>{p}</button>)}
      </div>
    </F>
    <div className="grid grid-cols-2 gap-3">
      <F title="Amount (₹)"><input data-testid="gp-amount" type="number" min="0" step="0.01" className={field + " num"} value={f.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="optional" /></F>
      {["qr", "bank", "upi"].includes(f.payment_type) && <F title="Reference number" req><input data-testid="gp-reference" className={field} value={f.reference} onChange={(e) => set({ reference: e.target.value })} placeholder="UTR / txn id" /></F>}
    </div>
  </>
);

const AccountPick = ({ f, set, accounts }) => {
  const sel = accounts.find((a) => a.name === f.account_name);
  const pick = (name) => {
    const a = accounts.find((x) => x.name === name);
    set({ account_name: name, ...(a ? { guarantor_name: a.guarantor_name || "", guarantor_mobile: a.guarantor_phone || "" } : {}) });
  };
  return (
    <F title="Credit account" req>
      <select data-testid="gp-account" className={field} value={f.account_name} onChange={(e) => pick(e.target.value)}>
        <option value="">Select credit account… ({accounts.length})</option>
        {accounts.map((a) => <option key={a.name} value={a.name}>{a.account_no ? `${a.account_no} · ` : ""}{a.name}</option>)}
      </select>
      {sel && <p data-testid="gp-account-code" className="text-xs mt-1">Account code: <b className="num">{sel.account_no || "—"}</b></p>}
    </F>
  );
};

const CreditFields = ({ f, set, gOtp, setGOtp, accounts, sign, setSig }) => (
  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-3">
    <AccountPick f={f} set={set} accounts={accounts} />
    <div className="grid grid-cols-2 gap-3">
      <F title="Guarantor name" req><input data-testid="gp-guarantor-name" className={field} value={f.guarantor_name} onChange={(e) => set({ guarantor_name: e.target.value })} /></F>
      <F title="Guarantor mobile" req><input data-testid="gp-guarantor-mobile" className={field + " num"} value={f.guarantor_mobile} onChange={(e) => { set({ guarantor_mobile: e.target.value }); setGOtp(""); }} placeholder="10-digit" /></F>
    </div>
    {sign ? <SignaturePad title="Guarantor signature" testid="gp-sig-guarantor" onChange={(v) => setSig({ guarantor: v })} />
      : <OtpVerify role="guarantor" mobile={f.guarantor_mobile} otpId={gOtp} onVerified={setGOtp} />}
  </div>
);

const OutsideVehicle = ({ f, set, meta }) => (
  <>
    <F title="Vehicle" req>
      <div className="grid grid-cols-2 gap-1.5">
        <button type="button" data-testid="gp-ow-company" onClick={() => set({ ow_vehicle: "company", vehicle_number: "" })} className={pill(f.ow_vehicle === "company")}>Company vehicle</button>
        <button type="button" data-testid="gp-ow-employee" onClick={() => set({ ow_vehicle: "employee", vehicle_number: "" })} className={pill(f.ow_vehicle === "employee")}>Employee vehicle</button>
      </div>
    </F>
    {f.ow_vehicle === "company" ? (
      <select data-testid="gp-ow-company-vehicle" className={field} value={f.vehicle_number} onChange={(e) => set({ vehicle_number: e.target.value })}>
        <option value="">Select company vehicle…</option>
        {meta.vehicles.map((v) => <option key={v.number} value={v.number}>{v.number}{v.name ? ` · ${v.name}` : ""}</option>)}
      </select>
    ) : <input data-testid="gp-ow-vehicle-no" className={field + " num"} value={f.vehicle_number} onChange={(e) => set({ vehicle_number: e.target.value.toUpperCase() })} placeholder="Employee vehicle number" />}
    <F title={`Current km${f.ow_vehicle === "company" ? "" : " (optional)"}`} req={f.ow_vehicle === "company"}><input data-testid="gp-ow-km" type="number" min="0" className={field + " num"} value={f.current_km} onChange={(e) => set({ current_km: e.target.value })} placeholder="Odometer reading" /></F>
  </>
);

const TrialFields = ({ f, set }) => (
  <div className="space-y-3">
    <F title="Vehicle number" req><Autocomplete field="vehicle" testid="gp-trial-vehicle" value={f.vehicle_number} onChange={(v) => set({ vehicle_number: v })} placeholder="DL01AB1234" /></F>
    <div className="grid grid-cols-2 gap-3">
      <F title="Mechanic name" req><input data-testid="gp-trial-mechanic" className={field} value={f.employee_name} onChange={(e) => set({ employee_name: e.target.value })} /></F>
      <F title="Mechanic mobile" req><input data-testid="gp-trial-mobile" className={field + " num"} value={f.driver_mobile} onChange={(e) => set({ driver_mobile: e.target.value })} placeholder="10-digit" /></F>
    </div>
    <F title="Remark"><input data-testid="gp-trial-remarks" className={field} value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} placeholder="optional" /></F>
  </div>
);

const StaffFields = ({ f, set, meta }) => f.gp_type === "trial" ? <TrialFields f={f} set={set} /> : (
  <div className="space-y-3">
    {f.gp_type === "outside_work" && <OutsideVehicle f={f} set={set} meta={meta} />}
    <F title="Employee name" req>
      <input data-testid="gp-employee-name" list="gp-employee-list" className={field} value={f.employee_name} onChange={(e) => set({ employee_name: e.target.value })} placeholder="Pick or type the employee" />
      <datalist id="gp-employee-list">{(meta.employees || []).map((n) => <option key={n} value={n} />)}</datalist>
    </F>
    <F title="Remark" req><input data-testid="gp-staff-remarks" className={field} value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} placeholder={f.gp_type === "outside_work" ? "Work / where going" : "Reason for going out"} /></F>
  </div>
);

const MethodPick = ({ method, setMethod }) => (
  <F title="Confirm with" req>
    <div className="grid grid-cols-2 gap-1.5">
      <button type="button" data-testid="gp-method-otp" onClick={() => setMethod("otp")} className={pill(method === "otp")}>OTP on mobile</button>
      <button type="button" data-testid="gp-method-signature" onClick={() => setMethod("signature")} className={pill(method === "signature")}>Signature on screen</button>
    </div>
  </F>
);

export const GatePassForm = ({ open, onClose, onCreated }) => {
  const brand = useBrand();
  const [f, setF] = useState(blank());
  const [meta, setMeta] = useState({ vehicles: [], job_card_example: "", next_number: "GP-…" });
  const [dOtp, setDOtp] = useState("");
  const [gOtp, setGOtp] = useState("");
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("gp_auto_print") !== "0");
  const toggleAuto = (v) => { setAutoPrint(v); localStorage.setItem("gp_auto_print", v ? "1" : "0"); };
  const [method, setMethodRaw] = useState("otp");
  const [sigs, setSigs] = useState({});
  const setMethod = (m) => { setMethodRaw(m); setSigs({}); };
  const setSig = (p) => setSigs((x) => ({ ...x, ...p }));
  const sign = method === "signature";
  const set = (p) => { setF((x) => ({ ...x, ...p })); if (p.payment_type && p.payment_type !== "credit") setSigs((x) => ({ ...x, guarantor: "" })); };
  useEffect(() => {
    if (!open) return;
    setF(blank()); setDOtp(""); setGOtp(""); setMethodRaw("otp"); setSigs({});
    api.get("/gatepass/meta").then((r) => setMeta(r.data)).catch((e) => console.error(e));
  }, [open]);

  const company = f.gp_type === "company_vehicle";
  const staffType = STAFF_T.includes(f.gp_type);
  const save = async () => {
    const credit = f.payment_type === "credit";
    if (staffType && f.gp_type === "trial") {
      if (!f.vehicle_number.trim()) return toast.error("Enter the vehicle number");
      if (!f.employee_name.trim()) return toast.error("Enter the mechanic name");
      if (f.driver_mobile.replace(/\D/g, "").length < 10) return toast.error("Enter the mechanic mobile number (10 digits)");
    } else if (staffType) {
      if (f.gp_type === "outside_work" && !f.vehicle_number) return toast.error(f.ow_vehicle === "company" ? "Pick the company vehicle" : "Enter the employee's vehicle number");
      if (f.gp_type === "outside_work" && f.ow_vehicle === "company" && f.current_km === "") return toast.error("Enter the current km");
      if (!f.employee_name.trim()) return toast.error("Enter the employee name");
      if (!f.remarks.trim()) return toast.error("Enter the remark");
    }
    if (!staffType && credit && !f.account_name) return toast.error("Pick the credit account");
    if (!staffType && !sign && !dOtp) return toast.error("Verify the driver mobile with OTP");
    if (!staffType && !sign && credit && !gOtp) return toast.error("Verify the guarantor mobile with OTP");
    if (!staffType && sign && !sigs.driver) return toast.error("Driver / customer signature is required");
    if (!staffType && sign && credit && !sigs.guarantor) return toast.error("Guarantor signature is required");
    if (!staffType && sign && !sigs.staff) return toast.error("Staff signature is required");
    const win = autoPrint ? window.open("", "_blank", "width=360,height=640") : null;
    if (win) win.document.write("<p style='font-family:sans-serif'>Creating gate pass…</p>");
    try {
      const { data } = await api.post("/gatepass", staffType
        ? { gp_type: f.gp_type, date: f.date, vehicle_number: ["outside_work", "trial"].includes(f.gp_type) ? f.vehicle_number : "", driver_mobile: f.gp_type === "trial" ? f.driver_mobile : "", ow_vehicle: f.ow_vehicle, current_km: f.gp_type === "outside_work" && f.current_km !== "" ? parseFloat(f.current_km) : null, employee_name: f.employee_name, remarks: f.remarks, payment_type: "none" }
        : { ...f, amount: parseFloat(f.amount) || 0, current_km: company ? parseFloat(f.current_km) : null, verify_method: method,
        driver_otp_id: sign ? "" : dOtp, guarantor_otp_id: sign ? "" : gOtp,
        driver_signature: sign ? sigs.driver : "", guarantor_signature: sign && credit ? sigs.guarantor : "", staff_signature: sign ? sigs.staff : "" });
      toast.success(`Gate pass ${data.number} created`);
      if (win) printThermal(data, thermalMm(), win, brand);
      if (!staffType) sendGpCopy(data);
      onCreated(data);
    } catch (e) { if (win) win.close(); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto sm:max-w-xl" onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle className="font-display text-2xl flex justify-between gap-3 pr-8">New Gate Pass <span className="num text-xs px-2 py-1 rounded bg-accent/15 text-accent">{meta.next_number}</span></DialogTitle></DialogHeader>
        {!meta.whatsapp_ready && !sign && !staffType && <div data-testid="gp-wa-missing" className="rounded-md bg-destructive/10 text-destructive text-xs p-2.5">OTP is not set up yet. Use "Signature on screen" instead.</div>}
        <div className="space-y-3">
          <F title="Gate pass type" req>
            <div className="grid grid-cols-3 gap-1.5">
              {TYPES.map((t) => <button key={t.v} type="button" data-testid={`gp-type-${t.v}`} onClick={() => set({ gp_type: t.v, vehicle_number: "", payment_type: ["company_vehicle", "no_work", ...STAFF_T].includes(t.v) ? "none" : t.v === "foc" ? "foc" : "cash" })} className={pill(f.gp_type === t.v)}>{t.l}</button>)}
            </div>
          </F>
          {staffType ? (
            <>
              <StaffFields f={f} set={set} meta={meta} />
              <F title="Date"><input data-testid="gp-staff-date" type="date" className={field + " num"} value={f.date} onChange={(e) => set({ date: e.target.value })} /></F>
            </>
          ) : (<>
          <VehicleFields f={f} set={set} meta={meta} />
          <div className="grid grid-cols-2 gap-3">
            <F title="Job card number" req={f.gp_type === "invoice"}><input data-testid="gp-jobcard" className={field + " num"} value={f.job_card_number} onChange={(e) => set({ job_card_number: e.target.value.toUpperCase() })} placeholder={meta.job_card_example || "Job card no."} /></F>
            {f.gp_type === "invoice" ? <F title="Invoice number"><input data-testid="gp-invoice-no" className={field + " num"} value={f.invoice_no} onChange={(e) => set({ invoice_no: e.target.value })} /></F>
              : <F title="Date"><input data-testid="gp-date" type="date" className={field + " num"} value={f.date} onChange={(e) => set({ date: e.target.value })} /></F>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F title="Driver name" req><input data-testid="gp-driver-name" className={field} value={f.driver_name} onChange={(e) => set({ driver_name: e.target.value })} /></F>
            <F title="Driver mobile" req><input data-testid="gp-driver-mobile" className={field + " num"} value={f.driver_mobile} onChange={(e) => { set({ driver_mobile: e.target.value }); setDOtp(""); }} placeholder="10-digit" /></F>
          </div>
          <MethodPick method={method} setMethod={setMethod} />
          {sign ? <SignaturePad key="sd" title="Driver / customer signature" testid="gp-sig-driver" onChange={(v) => setSig({ driver: v })} />
            : <OtpVerify role="driver" mobile={f.driver_mobile} otpId={dOtp} onVerified={setDOtp} />}
          {!company && <PaymentFields f={f} set={set} />}
          {f.payment_type === "credit" && <CreditFields f={f} set={set} gOtp={gOtp} setGOtp={setGOtp} accounts={meta.credit_accounts || []} sign={sign} setSig={setSig} />}
          <F title="Remarks"><input data-testid="gp-remarks" className={field} value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} placeholder="optional" /></F>
          {sign && <SignaturePad key="ss" title="Staff signature (you)" testid="gp-sig-staff" onChange={(v) => setSig({ staff: v })} />}
          <p className="text-xs text-muted-foreground">The payment is only recorded on the gate pass; it is not posted to the cash book.</p>
          </>)}
        </div>
        <DialogFooter className="items-center gap-3 sm:justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input data-testid="gp-auto-print" type="checkbox" checked={autoPrint} onChange={(e) => toggleAuto(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" /> Thermal print after create ({thermalMm()}mm)</label>
          <button data-testid="gp-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create gate pass</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
